/**
 * The signed-in app shell.
 *
 * Two columns: persistent `AppSidebar` on the left, scrollable `<main>`
 * on the right. React Router renders the active page inside the
 * `<Outlet />` slot, so every nested `/app/*` route inherits the shell.
 *
 * The header is intentionally empty. Search, notifications, and help all
 * moved off the top bar: notifications became a sidebar item and help
 * moved into the account menu, so the top of every page stays clean.
 *
 * Auth state is NOT read here. `components/layout/` cannot import from
 * `features/`, so the user object and the sign-out handler come in as
 * props, wired by `src/app/routes/app-shell-route.tsx`.
 */
import { Outlet } from 'react-router';

import { AppSidebar } from '@/components/layout/app-sidebar/app-sidebar';
import { useSidebarCollapsed } from '@/components/layout/app-sidebar/use-sidebar-collapsed';
import { type UserChipUser } from '@/components/layout/user-chip/user-chip';
import { ErrorBoundary } from '@/components/ui/error-boundary/error-boundary';

import styles from './app-shell-layout.module.css';

export interface AppShellLayoutProps {
  user: UserChipUser | null;
  onSignOut: () => void;
  /** Setup progress for the sidebar "Get Started" entry; null/undefined hides it. */
  setupProgress?: { done: number; total: number } | null;
  /** Plan line for the account chip; falls back to "Free Plan" when omitted. */
  planLabel?: string;
}

export function AppShellLayout({ user, onSignOut, setupProgress, planLabel }: AppShellLayoutProps) {
  const [collapsed, toggleCollapsed] = useSidebarCollapsed();
  return (
    <div
      className={[styles.shell, collapsed ? styles.shellCollapsed : ''].filter(Boolean).join(' ')}
    >
      <AppSidebar
        user={user}
        onSignOut={onSignOut}
        setupProgress={setupProgress}
        collapsed={collapsed}
        onToggleCollapse={toggleCollapsed}
        planLabel={planLabel}
      />

      <div className={styles.column}>
        <main className={styles.main}>
          <ErrorBoundary>
            <Outlet />
          </ErrorBoundary>
        </main>
      </div>
    </div>
  );
}

export default AppShellLayout;
